<template>
  <h2>{{ t("components.siteSettings.tips.title") }}</h2>

  <v-card elevation="3" class="ma-3 pa-3">
    <v-row no-gutters>
      <v-col :cols="mdAndDown ? 12 : 4">
        <v-switch
          v-model="tipsEnabled"
          :label="t('components.siteSettings.tips.enableTips')"
          inset
          color="success"
          class="mx-2"
        />
      </v-col>

      <v-col :cols="mdAndDown ? (xs ? 12 : 6) : 4">
        <v-select
          v-if="tipsEnabled"
          v-model="_settings.provider"
          :label="t('components.siteSettings.tips.selectProvider')"
          :items="tipsProviders"
          :error-messages="
            (v$.provider.$silentErrors.map((e: ErrorObject) => e.$message) as string[]) || []
          "
          class="mx-2"
          style="min-width: 200px"
        />
      </v-col>

      <v-col :cols="mdAndDown ? (xs ? 12 : 6) : 4">
        <v-text-field
          v-if="tipsEnabled && _settings.provider === 'tipjar'"
          v-model="_settings.tipjarShortcode"
          :label="t('components.siteSettings.tips.tipjarShortcode')"
          clearable
          :error-messages="
            (v$.tipjarShortcode.$silentErrors.map((e: ErrorObject) => e.$message) as string[]) || []
          "
          class="mx-2"
          style="min-width: 230px"
        />
      </v-col>

      <v-col cols="12">
        <v-checkbox
          v-if="tipsEnabled"
          v-model="_settings.skipTipsIfServiceChargeIncluded"
          :label="t('components.siteSettings.tips.skipTipsIfServiceCharge')"
          inset
          color="primary"
          class="mx-2"
        />
      </v-col>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          :loading="loading"
          :disabled="settingsUnchanged || v$.$invalid"
          @click="submitUpdatedTipConfig"
        >
          {{ t("components.siteSettings.tips.saveButton") }}
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup lang="ts">
import { Tips } from "@tableyeti/merchant-service";
import useVuelidate, { ErrorObject } from "@vuelidate/core";
import { requiredIf } from "@vuelidate/validators";
import { cloneDeep, isEqual } from "lodash-es";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { useApplicationStore } from "@/store/application";

const { t } = useI18n();

const { settings } = defineProps<{ settings: Tips }>();

const loading = ref(false);

const { xs, mdAndDown } = useDisplay();

const { api, currentMerchantId } = useYetipayApi();
const applicationStore = useApplicationStore();
const siteId = useRoute().params.siteId as string;

const tipsEnabled = ref(!!settings);
const _settings = ref<Tips>(cloneDeep(settings ?? {}));
const v$ = useVuelidate<Tips>(
  {
    provider: { required: requiredIf(() => tipsEnabled.value) },
    tipjarShortcode: {
      required: requiredIf(() => tipsEnabled.value && _settings.value.provider === "tipjar"),
    },
    skipTipsIfServiceChargeIncluded: {},
  },
  _settings.value,
);

const tipsProviders = [
  { title: t("components.siteSettings.tips.providers.tipjar"), value: "tipjar" },
  { title: t("components.siteSettings.tips.providers.yetipay"), value: "yetipay" },
];

const settingsToEmit = computed<Tips | undefined>(() =>
  tipsEnabled.value
    ? {
        provider: _settings.value.provider,
        tipjarShortcode: _settings.value.tipjarShortcode || undefined,
        skipTipsIfServiceChargeIncluded:
          _settings.value.skipTipsIfServiceChargeIncluded || undefined,
      }
    : undefined,
);
const settingsUnchanged = computed(
  () => isEqual(settings, settingsToEmit.value) && tipsEnabled.value === !!settings?.provider,
);

const emit = defineEmits(["settingsUpdate"]);

const submitUpdatedTipConfig = async () => {
  if (v$.value.$invalid) {
    return;
  }

  loading.value = true;
  try {
    await api.updateSiteTips(currentMerchantId, siteId, settingsToEmit.value ?? {});
    applicationStore.notifyUser({ message: t("sites.internalSettings.successMessage") });
  } catch (error) {
    console.error("Error updating site tips settings:", error);
    applicationStore.notifyUser({
      message: t("components.siteSettings.tips.errorMessage"),
      type: "error",
    });
    emit("settingsUpdate");
  } finally {
    loading.value = false;
  }
};
</script>
